import { useState, useEffect } from "react";

export default function AthensHeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm border-b border-gray-100" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-[70px]">
          <a href="https://fastpassportcenter.com" className="flex items-center">
            <img
              src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/02/Header.svg"
              alt="Fast Passport Center"
              className={`h-9 w-auto transition-all ${scrolled ? "" : "brightness-0 invert"}`}
            />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: "Services", href: "#services" },
              { label: "How It Works", href: "#how-it-works" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <a
              href="tel:8772530084"
              className={`hidden md:inline-flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"
              }`}
            >
              <i className="ri-phone-line"></i>877-253-0084
            </a>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] text-sm font-bold px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
            >
              Get Started
            </a>
            <button
              className="md:hidden flex items-center justify-center w-9 h-9"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl ${scrolled ? "text-[#1A365D]" : "text-white"}`}></i>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-3">
            {[
              { label: "Services", href: "#services" },
              { label: "How It Works", href: "#how-it-works" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-[#1A365D] hover:text-[#F6C344] py-1"
              >
                {item.label}
              </a>
            ))}
            <a href="tel:8772530084" className="block text-sm font-semibold text-[#1A365D] py-1">
              <i className="ri-phone-line mr-1.5"></i>877-253-0084
            </a>
          </div>
        )}
      </nav>

      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden bg-[#0F2240]">
        <img
          src="https://readdy.ai/api/search-image?query=The%20iconic%20iron%20Arch%20at%20the%20University%20of%20Georgia%20entrance%20in%20Athens%20Georgia%20with%20historic%20red%20brick%20campus%20buildings%20and%20white%20columns%20in%20the%20background%2C%20lush%20green%20tree-lined%20pathways%20with%20magnolia%20trees%2C%20warm%20golden%20afternoon%20sunlight%2C%20classic%20Southern%20college%20town%20atmosphere%20with%20rolling%20green%20hills%2C%20no%20people%20or%20text%20visible%2C%20professional%20architectural%20photography%20with%20rich%20warm%20tones%20and%20clear%20sky&width=1800&height=700&seq=athens-hero-v2&orientation=landscape"
          alt="Athens Georgia University of Georgia Arch and historic campus"
          className="absolute inset-0 w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-16 text-center">
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Emergency &amp; Expedited Passport Services in Athens, GA
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
            Passport expired before your trip? Need an emergency passport fast? We help Athens, UGA students, and northeast Georgia travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.
          </p>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap"
          >
            Start My Expedited Passport <i className="ri-arrow-right-line"></i>
          </a>

          {/* Trust signals */}
          <div className="mt-10 w-full">
            <div className="inline-flex flex-wrap items-center justify-center gap-x-8 gap-y-3 bg-[#1A2B4A]/80 backdrop-blur-sm border border-white/10 rounded-lg px-8 py-3 mx-auto">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5">
                  <i className="ri-star-fill text-[#F6C344] text-base"></i>
                  <i className="ri-star-fill text-[#F6C344] text-base"></i>
                  <i className="ri-star-fill text-[#F6C344] text-base"></i>
                  <i className="ri-star-fill text-[#F6C344] text-base"></i>
                  <i className="ri-star-fill text-[#F6C344] text-base"></i>
                </div>
                <span className="text-white font-bold text-sm">5.0</span>
                <span className="text-white/70 text-sm">Reviews</span>
              </div>
              <div className="hidden sm:block w-px h-5 bg-white/20"></div>
              <div className="flex items-center gap-2">
                <span className="text-white font-bold text-sm">BBB A+</span>
                <span className="text-white/70 text-sm">Accredited</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}